import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Image,
  Row,
  Spin,
  Tag,
  Tooltip,
  Typography,
  App,
} from "antd";
import { CloudUploadOutlined, CheckCircleOutlined, ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import {
  extractBundle,
  extractSlides,
  getSlides,
  getSlidesJob,
  replaceSlide,
} from "../../../services/slides/slides.service";
import type { SlideItem } from "../../../services/slides/slides.types";

const { Text } = Typography;

type GalleryState = "IDLE" | "EXTRACTING" | "COMPLETED" | "FAILED";
type BundleState = "IDLE" | "PROCESSING" | "DONE" | "FAILED";

const POLLING_INTERVAL_MS = 3000;
const MAX_REPLACE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

interface SlidesGalleryProps {
  resourceId: number;
  geniallyUrl: string;
  resourceTitle: string;
}

const SlidesGallery = ({
  resourceId,
  geniallyUrl,
  resourceTitle,
}: SlidesGalleryProps) => {
  const { notification } = App.useApp();
  const [galleryState, setGalleryState] = useState<GalleryState>("IDLE");
  const [slides, setSlides] = useState<SlideItem[]>([]);
  const [jobError, setJobError] = useState<string | null>(null);
  const [replacingIds, setReplacingIds] = useState<Set<number>>(new Set());
  const [bundleState, setBundleState] = useState<BundleState>("IDLE");
  const [bundleError, setBundleError] = useState<string | null>(null);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const bundlePollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingReplaceIdRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const stopBundlePolling = useCallback(() => {
    if (bundlePollingRef.current) {
      clearInterval(bundlePollingRef.current);
      bundlePollingRef.current = null;
    }
  }, []);

  const loadSlides = useCallback(async () => {
    const items = await getSlides(resourceId);
    setSlides(items);
    return items;
  }, [resourceId]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const job = await getSlidesJob(resourceId);
        if (job.status === "COMPLETED") {
          try {
            await loadSlides();
            stopPolling();
            setGalleryState("COMPLETED");
          } catch (error) {
            // Transient load failure: keep polling to retry.
            console.warn(
              "loadSlides failed after job completion, will retry:",
              error,
            );
          }
        } else if (job.status === "FAILED") {
          stopPolling();
          setJobError(job.error_message ?? "La extracción falló.");
          setGalleryState("FAILED");
        }
        // PENDING / IN_PROGRESS: keep polling
      } catch (error) {
        // network issue during polling — keep trying
        console.warn("getSlidesJob polling error:", error);
      }
    }, POLLING_INTERVAL_MS);
  }, [resourceId, stopPolling, loadSlides]);

  // On mount: load initial data in parallel
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const [items, job, bundleJob] = await Promise.all([
          getSlides(resourceId),
          getSlidesJob(resourceId, "IMAGE_EXTRACTION"),
          getSlidesJob(resourceId, "BUNDLE_EXTRACTION"),
        ]);

        if (cancelled) return;

        if (items.length > 0 && job.status === "COMPLETED") {
          setSlides(items);
          setGalleryState("COMPLETED");
        } else if (job.status === "IN_PROGRESS" || job.status === "PENDING") {
          setGalleryState("EXTRACTING");
          startPolling();
        } else if (job.status === "FAILED") {
          setSlides(items); // show whatever slides exist
          setJobError(job.error_message ?? "La extracción anterior falló.");
          setGalleryState(items.length > 0 ? "COMPLETED" : "FAILED");
        } else {
          // NONE or no slides yet
          setGalleryState("IDLE");
        }

        // Bundle job initial state
        if (bundleJob.status === "PENDING" || bundleJob.status === "IN_PROGRESS") {
          setBundleState("PROCESSING");
          startBundlePolling();
        } else if (bundleJob.status === "COMPLETED") {
          setBundleState("DONE");
        } else if (bundleJob.status === "FAILED") {
          setBundleError(bundleJob.error_message ?? "La extracción del bundle falló.");
          setBundleState("FAILED");
        }
      } catch {
        if (!cancelled) setGalleryState("IDLE");
      }
    };

    init();

    return () => {
      cancelled = true;
      stopPolling();
      stopBundlePolling();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceId]);

  const handleExtract = async () => {
    if (!geniallyUrl) {
      notification.error({
        message: "Ingresá la URL de Genially antes de extraer.",
      });
      return;
    }

    try {
      setGalleryState("EXTRACTING");
      setJobError(null);
      await extractSlides({ resourceId, geniallyUrl, resourceTitle });
      startPolling();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? "No se pudo iniciar la extracción.";
      notification.error({ message: msg });
      setJobError(msg);

      if (!slides || slides.length === 0) {
        setGalleryState("IDLE");
      } else {
        // Mantenemos la galería si ya hay datos válidos
        setGalleryState("COMPLETED");
      }
    }
  };

  // ── Bundle extraction ─────────────────────────────────────────────────────

  const startBundlePolling = useCallback(() => {
    stopBundlePolling();
    bundlePollingRef.current = setInterval(async () => {
      try {
        const job = await getSlidesJob(resourceId, "BUNDLE_EXTRACTION");
        if (job.status === "COMPLETED") {
          stopBundlePolling();
          setBundleState("DONE");
          notification.success({ message: "Bundle offline actualizado correctamente." });
        } else if (job.status === "FAILED") {
          stopBundlePolling();
          setBundleError(job.error_message ?? "La extracción del bundle falló.");
          setBundleState("FAILED");
        }
      } catch {
        // network issue — keep polling
      }
    }, POLLING_INTERVAL_MS);
  }, [resourceId, stopBundlePolling, notification]);

  const handleExtractBundle = async () => {
    if (!geniallyUrl) {
      notification.error({ message: "Ingresá la URL de Genially antes de actualizar." });
      return;
    }
    try {
      setBundleState("PROCESSING");
      setBundleError(null);
      await extractBundle({ resourceId, geniallyUrl, resourceTitle });
      startBundlePolling();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "No se pudo iniciar la extracción del bundle.";
      notification.error({ message: msg });
      setBundleError(msg);
      setBundleState("FAILED");
    }
  };

  // ── Replace handlers ──────────────────────────────────────────────────────

  const handleReplaceClick = (slideId: number) => {
    if (replacingIds.has(slideId)) return;
    pendingReplaceIdRef.current = slideId;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    const slideId = pendingReplaceIdRef.current;

    // Reset input so the same file can be picked again later
    e.target.value = "";

    if (!file || slideId == null) return;

    if (file.size > MAX_REPLACE_SIZE_BYTES) {
      notification.error({ message: "El archivo debe ser menor a 5 MB." });
      return;
    }

    setReplacingIds((prev) => new Set(prev).add(slideId));
    try {
      const updated = await replaceSlide(slideId, file);
      // Apply cache-bust to the returned URL
      const bustUrl = `${updated.gcsUrl}?v=${Date.now()}`;
      setSlides((prev) =>
        prev.map((s) =>
          s.id === slideId ? { ...updated, gcsUrl: bustUrl } : s,
        ),
      );
      notification.success({ message: "Imagen reemplazada correctamente." });
    } catch (err: any) {
      notification.error({
        message:
          err?.response?.data?.message ??
          err?.message ??
          "Error al reemplazar la imagen.",
      });
    } finally {
      setReplacingIds((prev) => {
        const next = new Set(prev);
        next.delete(slideId);
        return next;
      });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (galleryState === "IDLE") {
    return (
      <div style={{ marginBottom: 16 }}>
        <Button
          icon={<SyncOutlined />}
          onClick={handleExtract}
          disabled={!geniallyUrl}
        >
          Extraer diapositivas
        </Button>
        {!geniallyUrl && (
          <Text type="secondary" style={{ marginTop: 6, display: "block" }}>
            Ingresá la URL de Genially para habilitar la extracción.
          </Text>
        )}
        <BundleSection
          bundleState={bundleState}
          bundleError={bundleError}
          disabled={!geniallyUrl}
          onExtract={handleExtractBundle}
        />
      </div>
    );
  }

  if (galleryState === "EXTRACTING") {
    return (
      <div style={{ marginBottom: 16 }}>
        <Spin tip="Extrayendo diapositivas, esto puede tardar varios minutos…">
          <div style={{ height: 60 }} />
        </Spin>
        <BundleSection
          bundleState={bundleState}
          bundleError={bundleError}
          disabled={!geniallyUrl}
          onExtract={handleExtractBundle}
        />
      </div>
    );
  }

  if (galleryState === "FAILED") {
    return (
      <>
        <Alert
          type="error"
          message="La extracción falló"
          description={jobError ?? undefined}
          action={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleExtract}
              disabled={!geniallyUrl}
            >
              Reintentar
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
        <BundleSection
          bundleState={bundleState}
          bundleError={bundleError}
          disabled={!geniallyUrl}
          onExtract={handleExtractBundle}
        />
      </>
    );
  }

  // COMPLETED
  return (
    <div style={{ marginBottom: 16 }}>
      {jobError && (
        <Alert
          type="warning"
          message="La última re-extracción falló"
          description={jobError}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <Text strong style={{ whiteSpace: "nowrap" }}>
          {slides.length} diapositiva{slides.length !== 1 ? "s" : ""}
        </Text>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={handleExtract}
          disabled={!geniallyUrl}
          style={{ flex: "0 0 auto", minWidth: 115 }}
        >
          Re-extraer
        </Button>
      </div>

      {/* Hidden file input for replace */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />

      <Image.PreviewGroup>
        <Row gutter={[16, 16]}>
          {slides.map((slide) => (
            <Col key={slide.id} xs={24} sm={12} md={8} lg={6}>
              <Card
                size="small"
                cover={
                  <div style={{ position: "relative" }}>
                    <Image
                      src={slide.gcsUrl}
                      alt={slide.fileName}
                      style={{
                        width: "100%",
                        objectFit: "contain",
                        maxHeight: 200,
                      }}
                      preview={{
                        mask: <span style={{ fontSize: 13 }}>Ver imagen</span>,
                      }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        top: 4,
                        left: 4,
                        background: "rgba(0,0,0,0.55)",
                        color: "#fff",
                        borderRadius: 4,
                        padding: "0 6px",
                        fontSize: 12,
                        pointerEvents: "none",
                      }}
                    >
                      {slide.pageNumber}
                    </span>
                  </div>
                }
                bodyStyle={{ padding: "8px 10px" }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    display: "block",
                    marginBottom: 6,
                    lineHeight: 1.2,
                  }}
                >
                  {slide.fileName}
                </Text>
                <Button
                  size="small"
                  style={{ width: "auto", minWidth: 100, maxWidth: 130 }}
                  loading={replacingIds.has(slide.id)}
                  disabled={replacingIds.has(slide.id)}
                  onClick={() => handleReplaceClick(slide.id)}
                >
                  Sustituir
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      </Image.PreviewGroup>

      <BundleSection
        bundleState={bundleState}
        bundleError={bundleError}
        disabled={!geniallyUrl}
        onExtract={handleExtractBundle}
      />
    </div>
  );
};

// ── BundleSection ─────────────────────────────────────────────────────────────────────

interface BundleSectionProps {
  bundleState: BundleState;
  bundleError: string | null;
  disabled: boolean;
  onExtract: () => void;
}

const BundleSection = ({ bundleState, bundleError, disabled, onExtract }: BundleSectionProps) => {
  const isProcessing = bundleState === "PROCESSING";

  return (
    <>
      <Divider style={{ margin: "16px 0 12px" }} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text strong>Bundle offline</Text>
          {bundleState === "DONE" && (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Actualizado
            </Tag>
          )}
          {bundleState === "FAILED" && (
            <Tooltip title={bundleError}>
              <Tag color="error">Falló</Tag>
            </Tooltip>
          )}
          {bundleState === "PROCESSING" && (
            <Tag color="processing">Generando…</Tag>
          )}
        </div>
        <Tooltip title="Genera el bundle HTML interactivo de Genially para modo offline">
          <Button
            size="small"
            icon={<CloudUploadOutlined />}
            loading={isProcessing}
            disabled={disabled || isProcessing}
            onClick={onExtract}
          >
            {bundleState === "FAILED" ? "Reintentar" : "Actualizar"}
          </Button>
        </Tooltip>
      </div>
    </>
  );
};

export default SlidesGallery;
